<template>
  <q-card class="price-history-card">
    <q-card-section v-if="loading" class="q-pa-lg">
      <div class="column q-gutter-y-md">
        <q-skeleton height="60px" animation="fade" />
        <q-skeleton height="200px" animation="fade" />
      </div>
    </q-card-section>

    <div v-else>
      <q-card-section>
        <div class="flex items-center q-mb-sm">
          <q-icon name="sym_r_car_tag" size="26px" class="q-mr-sm" />
          <h5 class="q-mb-none">Pricing History</h5>
        </div>

        <p v-if="priceDifference" class="price-summary q-mb-none">
          <span :class="priceDifferenceClass" class="text-weight-bold">
            ${{ formatNumber(Math.abs(priceDifference)) }}
          </span>
          <span class="text-text-light q-ml-xs">
            {{ priceDifference &lt; 0 ? 'below' : 'above' }} the original list
            price.
          </span>
        </p>
        <p v-else class="price-summary q-mb-none text-text-light">
          No price changes yet.
        </p>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div class="price-history-table">
          <div class="price-history-header">
            <div class="header-cell header-cell--date">Date</div>
            <div class="header-cell header-cell--change">Change</div>
            <div class="header-cell header-cell--price">Price</div>
          </div>

          <div class="price-history-body">
            <div
              v-for="(entry, i) in displayPriceHistory"
              :key="i + '_priceHistoryRow'"
              class="price-history-row"
            >
              <div
                class="row-cell row-cell--date"
                :class="{ 'text-text-light': !entry.time_created }"
              >
                {{ entry.time_created ? formatDate(entry.time_created) : "-" }}
              </div>
              <div class="row-cell row-cell--change">
                <PriceChangeIndicator
                  v-if="entry.old_price !== null"
                  :price-change="
                    parseFloat(entry.new_price) - parseFloat(entry.old_price)
                  "
                />
                <div v-else class="listed-badge">
                  <q-icon
                    name="sym_r_sell"
                    size="17px"
                    style="margin-bottom: -2px"
                  />
                  <span>Listed</span>
                </div>
              </div>
              <div class="row-cell row-cell--price">
                ${{ formatNumber(parseFloat(entry.new_price)) }}
              </div>
            </div>
          </div>
        </div>

        <div class="disclaimer q-mt-md q-mb-none">
          Total price amount is based on price change information provided by
          the seller.
        </div>
      </q-card-section>
    </div>
  </q-card>
</template>

<script>
import axios from "axios";
import vehicleHelpers from "@/mixins/vehicleHelpers";
import PriceChangeIndicator from "@/components/UI/PriceChangeIndicator.vue";

export default {
  name: "VehiclePriceHistory",
  mixins: [vehicleHelpers],
  components: {
    PriceChangeIndicator
  },
  props: {
    productId: {
      type: [Number, String],
      required: true
    },
    price: {
      type: [Number, String],
      required: true
    },
    dateOnLot: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      loading: true,
      priceHistory: null,
      priceHistoryDetails: null,
      displayPriceHistory: [],
      priceDifference: null,
      priceDifferenceClass: ""
    };
  },
  mounted() {
    this.fetchPriceHistory();
  },
  methods: {
    fetchPriceHistory() {
      this.loading = true;
      axios
        .get(
          "https://api-v2.jumpfeed.com/network/publisher/products/vehicles/price-history?product_id=" +
            this.productId
        )
        .then(response => {
          if (response.data && response.data.data) {
            this.priceHistory = response.data.data;

            // Set all data properties after fetch completes
            this.priceHistoryDetails = this.getVehiclePriceHistoryDetails(
              this.priceHistory,
              this.price,
              this.dateOnLot
            );
            this.displayPriceHistory = this.priceHistoryDetails.full;

            const totalChange = this.priceHistoryDetails.total.amount_change;
            this.priceDifference = totalChange !== 0 ? totalChange : null;
            this.priceDifferenceClass = this.priceDifference
              ? this.priceDifference < 0
                ? "text-positive"
                : "text-negative"
              : "";
          }
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatDate(dateString) {
      return this.$dayjs(dateString).format("MM/DD/YY");
    }
  }
};
</script>

<style lang="scss" scoped>
.price-history-card {
  background: color(background, light);
  border: 1px solid color(border);

  .price-summary {
    font-size: 15px;
    line-height: 1.4;
  }

  .price-history-table {
    background: color(background, very-dark);
    border-radius: 8px;
    border: 1px solid color(border);
    overflow: hidden;
    box-shadow: $shadow-bottom;

    .price-history-header {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      background: color(background, very-light);
      padding: 10px $space-md;
      border-bottom: 1px solid color(border);

      .header-cell {
        font-size: 13px;
        font-weight: 700;
        color: color(text);

        &--date {
          text-align: left;
        }

        &--change {
          text-align: center;
        }

        &--price {
          text-align: right;
        }
      }
    }

    .price-history-body {
      .price-history-row {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        padding: $space-md $space-md;
        border-bottom: 1px solid color(border);

        &:last-child {
          border-bottom: none;
        }

        .row-cell {
          display: flex;
          align-items: center;
          font-size: 15px;

          &--date {
            justify-content: flex-start;
            font-weight: 600;
            color: color(text, default);
          }

          &--change {
            justify-content: center;

            .listed-badge {
              display: flex;
              align-items: center;
              gap: 6px;
              color: color(text, light);
              font-weight: 500;
            }
          }

          &--price {
            justify-content: flex-end;
            font-weight: 700;
            font-size: 16px;
            color: color(text, default);
          }
        }
      }
    }
  }
}
</style>
